// src/App.js
import { Helmet } from "react-helmet";
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
} from "react-router-dom";
import AnalyticsInjector from "./components/AnalyticsInjector";
import "./styles/liveEdit.css";
import "./styles/main.css";

//components
import MenuManagement from "./components/admin/MenuManagement";
import FloatingContactButton from "./components/FloatingContactButton";
import Footer from "./components/Footer";
import GlobalNotification from "./components/GlobalNotification";
import Navbar from "./components/Navbar";
import ScrollToTop from "./components/ScrollToTop";
import SitemapXml from "./components/SitemapXml";
import CategoryPage from "./pages/CategoryPage";

//Context
import { AuthProvider } from "./context/AuthContext";
import { CartProvider } from "./context/CartContext";
import { ConfigProvider } from "./context/ConfigContext";
import { ConfigStylesProvider } from "./context/ConfigStylesProvider";
import { NotificationProvider } from "./context/NotificationContext";

//pages
// import Home from "./pages/Home";
// import AboutUs from "./pages/AboutUs";
// import Contact from "./pages/Contact";
// import Product from "./pages/Product";
// import PrivacyPolicy from "./pages/PrivacyPolicy";
// import TermsConditions from "./pages/TermsConditions";
import ClientUploads from "./pages/admin/ClientUploads";
import AdminDashboard from "./pages/AdminDashboard";
import AuctionListPage from "./pages/AuctionListPage";
import AuctionPage from "./pages/AuctionPage";
import Cart from "./pages/Cart";
import ForgotPassword from "./pages/ForgotPassword";
import Login from "./pages/Login";
import NotificationsPage from "./pages/NotificationsPage";
import OptimizedHomePage from "./pages/OptimizedHomePage";
import Orders from "./pages/Orders";
import Page from "./pages/Page";
import ProductCustomization from "./pages/ProductCustomization";
import Profile from "./pages/Profile";
import Register from "./pages/Register";
import ResetPassword from "./pages/ResetPassword";
import Support from "./pages/Support";
import TeamMemberDetail from "./pages/TeamMemberDetail";
import Unsubscribe from "./pages/Unsubscribe";

// Blog Pages
import Blog from "./pages/blog/Blog";
import BlogCategory from "./pages/blog/BlogCategory";
import BlogPost from "./pages/blog/BlogPost";
import BlogTag from "./pages/blog/BlogTag";

// Blog Management Components
import CategoriesManagement from "./components/admin/blog/CategoriesManagement";
import BlogDashboard from "./components/admin/blog/Dashboard";
import PostForm from "./components/admin/blog/PostForm";
import PostsManagement from "./components/admin/blog/PostsManagement";
import TagsManagement from "./components/admin/blog/TagsManagement";
import InvoiceManagement from "./components/admin/InvoiceManagement";
import NewClientForm from "./components/admin/NewClientForm";
import InvoicePayment from "./pages/InvoicePayment";

// Booking Pages
import Blogs from "./pages/Blogs";
import BookingConfirmation from "./pages/BookingConfirmation";
import BookingPage from "./pages/BookingPage";
import ContactUs from "./pages/ContactUs";
import Gallery from "./pages/Gallery";
import Reviews from "./pages/Reviews";
import RoadTestBooking from "./pages/RoadTestBooking";

import MigrationTool from "./pages/MigrationTool";

// import AuthError from './pages/AuthError';
// import AuthSuccess from './pages/AuthSuccess';

// import ConfigDebugger from "./components/ConfigDebugger";

function App() {
  // Check if current hostname contains 'dev.' to hide from search engines
  const isDevEnvironment = window.location.hostname.includes("dev.");

  return (
    <AuthProvider>
      <CartProvider>
        <NotificationProvider>
          <ConfigProvider>
            <ConfigStylesProvider>
              <Router>
                <AnalyticsInjector />
                {/* <DynamicConfigUpdater /> Add this component */}
                {/* <ConfigDebugger /> Add this line */}
                <ScrollToTop />
                {/* Conditionally add meta tag to prevent search engine indexing for any dev branch */}
                {isDevEnvironment && (
                  <Helmet>
                    <meta name="robots" content="noindex, nofollow" />
                  </Helmet>
                )}
                <div className="min-h-screen bg-gray-50">
                  <Navbar menuName="navbar" fallbackMenuName="main" />
                  <GlobalNotification />
                  <main
                    className="container mx-auto px-4 py-8 min-h-screen"
                    style={{ minWidth: 100 + "%", padding: 0 }}
                  >
                    <Routes>
                      <Route path="/" element={<OptimizedHomePage />} />
                      <Route path="/:slug" element={<Page />} />
                      {/* <Route path="/" element={
                        <PageBuilderRenderer pageKey="home" fallback={<Home />} />
                      } /> */}
                      {/* <Route path="/" element={<Home />} /> */}
                      {/* <Route path="/product/:productName" element={<ProductCustomization />} /> */}
                      {/* <Route path="/product/:productSlug" element={<ProductCustomization />} /> */}
                      {/* <Route path="/product/:productName/:id" element={<ProductCustomization />} /> */}
                      {/* Home route now uses PageBuilderRenderer with fallback to Home component */}

                      <Route
                        path="/product/:productSlug"
                        element={<ProductCustomization />}
                      />

                      {/* <Route path="/customize/:productId" element={<ProductCustomization />} /> */}
                      {/* <Route path="/customize/:productId" element={<ProductEditor />} /> */}
                      {/* <Route path="/customize/:productId" element={<ProductEditorRoute />} /> */}
                      <Route path="/login" element={<Login />} />
                      <Route path="/register" element={<Register />} />
                      <Route path="/cart" element={<Cart />} />
                      <Route path="/pitech" element={<AdminDashboard />} />
                      <Route
                        path="/pitech/auctions"
                        element={
                          <AdminDashboard initialActiveMenu="auctionManagement" />
                        }
                      />
                      <Route path="/profile" element={<Profile />} />
                      <Route path="/orders" element={<Orders />} />
                      <Route
                        path="/cat/:categorySlug"
                        element={<CategoryPage />}
                      />

                      <Route
                        path="/team/:slug"
                        element={<TeamMemberDetail />}
                      />
                      <Route path="/support" element={<Support />} />
                      <Route
                        path="/notifications"
                        element={<NotificationsPage />}
                      />
                      <Route path="/auction/:id" element={<AuctionPage />} />
                      <Route
                        path="/auctions/:id"
                        element={<AuctionPage />}
                      />
                      <Route
                        path="/auction-list"
                        element={<AuctionListPage />}
                      />
                      <Route
                        path="/forgot-password"
                        element={<ForgotPassword />}
                      />
                      <Route
                        path="/reset-password/:token"
                        element={<ResetPassword />}
                      />
                      <Route
                        path="/unsubscribe/:token"
                        element={<Unsubscribe />}
                      />

                      <Route path="/blog" element={<Blog />} />
                      <Route path="/blog/:slug" element={<BlogPost />} />
                      <Route
                        path="/blog/category/:slug"
                        element={<BlogCategory />}
                      />
                      <Route path="/blog/tag/:slug" element={<BlogTag />} />

                      {/* Admin dashboard */}
                      <Route
                        path="/pitech/clients/new"
                        element={<NewClientForm />}
                      />
                      <Route
                        path="/pitech/uploads"
                        element={<ClientUploads />}
                      />
                      <Route
                        path="/pitech/menus"
                        element={<MenuManagement />}
                      />
                      {/* <Route path="/pitech/pages" element={<PageManagement />} /> 
                      <Route path="/pitech/pages/:id" element={<PageManagement />} />  */}
                      <Route
                        path="/pitech/pages"
                        element={
                          <AdminDashboard initialActiveMenu="pageManagement" />
                        }
                      />
                      <Route
                        path="/pitech/pages/:id"
                        element={
                          <AdminDashboard initialActiveMenu="pageManagement" />
                        }
                      />
                      {/* Blog Management Routes */}
                      <Route
                        path="/pitech/blog"
                        element={<AdminDashboard initialActiveMenu="blogManagement" />}
                      />
                      <Route
                        path="/pitech/blog/dashboard"
                        element={<BlogDashboard />}
                      />
                      <Route
                        path="/pitech/blog/posts"
                        element={<PostsManagement />}
                      />
                      <Route
                        path="/pitech/blog/posts/new"
                        element={<PostForm />}
                      />
                      <Route
                        path="/pitech/blog/posts/edit/:id"
                        element={<PostForm />}
                      />
                      <Route
                        path="/pitech/blog/categories"
                        element={<CategoriesManagement />}
                      />
                      <Route
                        path="/pitech/blog/tags"
                        element={<TagsManagement />}
                      />

                      {/* Invoice Routes */}
                      <Route path="/invoices" element={<InvoiceManagement />} />
                      <Route
                        path="/invoice/pay/:paymentId"
                        element={<InvoicePayment />}
                      />

                      {/* Booking Routes */}
                      <Route path="/book/:slug" element={<BookingPage />} />
                      <Route
                        path="/booking-confirmation/:bookingNumber"
                        element={<BookingConfirmation />}
                      />
                      <Route path="/road-test-booking" element={<RoadTestBooking />} />

                      {/* Reviews Route */}
                      <Route path="/student-reviews" element={<Reviews />} />

                      {/* Contact Us Route */}
                      <Route path="/contactUs" element={<ContactUs />} />

                      {/* Blogs Route */}
                      <Route path="/blogs" element={<Blogs />} />

                      {/* Gallery Route */}
                      <Route path="/gallery" element={<Gallery />} />

                      {/* Admin Booking Routes */}
                      <Route
                        path="/pitech/bookings"
                        element={<AdminDashboard initialActiveMenu="bookingManagement" />}
                      />

                      {!(
                        process.env.NODE_ENV === "dev" ||
                        window.location.hostname === "dev.bagbox.ca"
                      ) && (
                          <>
                            <Route path="/sitemap.xml" element={<SitemapXml />} />
                            <Route path="/sitemap" element={<SitemapXml />} />
                          </>
                        )}

                      <Route
                        path="/admin/*"
                        element={
                          <Navigate
                            to={window.location.pathname.replace(
                              /^\/admin/,
                              "/pitech"
                            )}
                            replace
                          />
                        }
                      />

                      <Route path="/migration-tool" element={<MigrationTool />} />
                      {/* Google OAuth Routes */}
                      {/* <Route path="/pitech/auth-success" element={<AuthSuccess />} />
                      <Route path="/pitech/auth-error" element={<AuthError />} /> */}
                    </Routes>
                  </main>
                  <Footer style={{}} />
                </div>
                <FloatingContactButton />
              </Router>
            </ConfigStylesProvider>
          </ConfigProvider>
        </NotificationProvider>
      </CartProvider>
    </AuthProvider>
  );
}

export default App;

// // before liev edit
// // src/App.js
// import { Helmet } from "react-helmet";
// import {
//   Navigate,
//   Route,
//   BrowserRouter as Router,
//   Routes,
// } from "react-router-dom";
// import AnalyticsInjector from "./components/AnalyticsInjector";
// import "./styles/main.css";

// //components
// import BackToTopButton from "./components/BackToTopButton";
// import Footer from "./components/Footer";
// import GlobalNotification from "./components/GlobalNotification";
// import Navbar from "./components/Navbar";
// import ScrollToTop from "./components/ScrollToTop";
// import SitemapXml from "./components/SitemapXml";
// import CategoryPage from "./pages/CategoryPage";

// import MenuManagement from "./components/admin/MenuManagement";

// //Context
// import { AuthProvider } from "./context/AuthContext";
// import { CartProvider } from "./context/CartContext";
// import { ConfigProvider } from "./context/ConfigContext";
// import { ConfigStylesProvider } from "./context/ConfigStylesProvider";
// import { NotificationProvider } from "./context/NotificationContext";

// //pages
// // import Home from "./pages/Home";
// // import AboutUs from "./pages/AboutUs";
// // import Contact from "./pages/Contact";
// // import Product from "./pages/Product";
// // import PrivacyPolicy from "./pages/PrivacyPolicy";
// // import TermsConditions from "./pages/TermsConditions";
// import ClientUploads from "./pages/admin/ClientUploads";
// import AdminDashboard from "./pages/AdminDashboard";
// import Cart from "./pages/Cart";
// import ForgotPassword from "./pages/ForgotPassword";
// import Login from "./pages/Login";
// import NotificationsPage from "./pages/NotificationsPage";
// import OptimizedHomePage from "./pages/OptimizedHomePage";
// import Orders from "./pages/Orders";
// import Page from "./pages/Page";
// import ProductCustomization from "./pages/ProductCustomization";
// import Profile from "./pages/Profile";
// import Register from "./pages/Register";
// import ResetPassword from "./pages/ResetPassword";
// import Support from "./pages/Support";
// import TeamMemberDetail from "./pages/TeamMemberDetail";
// import TeamPage from "./pages/TeamPage";
// import Unsubscribe from "./pages/Unsubscribe";

// // Blog Pages
// import Blog from "./pages/blog/Blog";
// import BlogCategory from "./pages/blog/BlogCategory";
// import BlogPost from "./pages/blog/BlogPost";
// import BlogTag from "./pages/blog/BlogTag";

// // Blog Management Components
// import CategoriesManagement from "./components/admin/blog/CategoriesManagement";
// import BlogDashboard from "./components/admin/blog/Dashboard";
// import PostForm from "./components/admin/blog/PostForm";
// import PostsManagement from "./components/admin/blog/PostsManagement";
// import TagsManagement from "./components/admin/blog/TagsManagement";
// import BlogManagement from "./components/admin/BlogManagement";
// import InvoiceManagement from "./components/admin/InvoiceManagement";
// import NewClientForm from "./components/admin/NewClientForm";
// import InvoicePayment from "./pages/InvoicePayment";

// // Booking Pages
// import BookingManagement from "./components/admin/BookingManagement";
// import BookingConfirmation from "./pages/BookingConfirmation";
// import BookingPage from "./pages/BookingPage";

// // import AuthError from './pages/AuthError';
// // import AuthSuccess from './pages/AuthSuccess';

// function App() {
//   // Check if current hostname contains 'dev.' to hide from search engines
//   const isDevEnvironment = window.location.hostname.includes("dev.");

//   return (
//     <AuthProvider>
//       <CartProvider>
//         <NotificationProvider>
//           <ConfigProvider>
//             <ConfigStylesProvider>
//               <Router>
//                 <AnalyticsInjector />
//                 <ScrollToTop />
//                 {/* Conditionally add meta tag to prevent search engine indexing for any dev branch */}
//                 {isDevEnvironment && (
//                   <Helmet>
//                     <meta name="robots" content="noindex, nofollow" />
//                   </Helmet>
//                 )}
//                 <div className="min-h-screen bg-gray-50">
//                   <Navbar menuName="navbar" fallbackMenuName="main" />
//                   <GlobalNotification />
//                   <main
//                     className="container mx-auto px-4 py-8 min-h-screen"
//                     style={{ minWidth: 100 + "%", padding: 0 }}
//                   >
//                     <Routes>
//                       <Route path="/" element={<OptimizedHomePage />} />
//                       <Route path="/:slug" element={<Page />} />
//                       {/* <Route path="/" element={
//                         <PageBuilderRenderer pageKey="home" fallback={<Home />} />
//                       } /> */}
//                       {/* <Route path="/" element={<Home />} /> */}
//                       {/* <Route path="/product/:productName" element={<ProductCustomization />} /> */}
//                       {/* <Route path="/product/:productSlug" element={<ProductCustomization />} /> */}
//                       {/* <Route path="/product/:productName/:id" element={<ProductCustomization />} /> */}
//                       {/* Home route now uses PageBuilderRenderer with fallback to Home component */}

//                       <Route
//                         path="/product/:productSlug"
//                         element={<ProductCustomization />}
//                       />

//                       {/* <Route path="/customize/:productId" element={<ProductCustomization />} /> */}
//                       {/* <Route path="/customize/:productId" element={<ProductEditor />} /> */}
//                       {/* <Route path="/customize/:productId" element={<ProductEditorRoute />} /> */}
//                       <Route path="/login" element={<Login />} />
//                       <Route path="/register" element={<Register />} />
//                       <Route path="/cart" element={<Cart />} />
//                       <Route path="/pitech" element={<AdminDashboard />} />
//                       <Route path="/profile" element={<Profile />} />
//                       <Route path="/orders" element={<Orders />} />
//                       <Route
//                         path="/cat/:categorySlug"
//                         element={<CategoryPage />}
//                       />

//                       <Route path="/team/" element={<TeamPage />} />
//                       <Route
//                         path="/team/:slug"
//                         element={<TeamMemberDetail />}
//                       />
//                       <Route path="/support" element={<Support />} />
//                       <Route
//                         path="/notifications"
//                         element={<NotificationsPage />}
//                       />

//                       <Route
//                         path="/forgot-password"
//                         element={<ForgotPassword />}
//                       />
//                       <Route
//                         path="/reset-password/:token"
//                         element={<ResetPassword />}
//                       />
//                       <Route
//                         path="/unsubscribe/:token"
//                         element={<Unsubscribe />}
//                       />

//                       <Route path="/blog" element={<Blog />} />
//                       <Route path="/blog/:slug" element={<BlogPost />} />
//                       <Route
//                         path="/blog/category/:slug"
//                         element={<BlogCategory />}
//                       />
//                       <Route path="/blog/tag/:slug" element={<BlogTag />} />

//                       {/* Admin dashboard */}
//                       <Route
//                         path="/pitech/clients/new"
//                         element={<NewClientForm />}
//                       />
//                       <Route
//                         path="/pitech/uploads"
//                         element={<ClientUploads />}
//                       />
//                       <Route
//                         path="/pitech/menus"
//                         element={<MenuManagement />}
//                       />
//                       {/* <Route path="/pitech/pages" element={<PageManagement />} />
//                       <Route path="/pitech/pages/:id" element={<PageManagement />} />  */}
//                       <Route
//                         path="/pitech/pages"
//                         element={
//                           <AdminDashboard initialActiveMenu="pageManagement" />
//                         }
//                       />
//                       <Route
//                         path="/pitech/pages/:id"
//                         element={
//                           <AdminDashboard initialActiveMenu="pageManagement" />
//                         }
//                       />
//                       {/* Blog Management Routes */}
//                       <Route path="/pitech/blog" element={<BlogManagement />} />
//                       <Route
//                         path="/pitech/blog/dashboard"
//                         element={<BlogDashboard />}
//                       />
//                       <Route
//                         path="/pitech/blog/posts"
//                         element={<PostsManagement />}
//                       />
//                       <Route
//                         path="/pitech/blog/posts/new"
//                         element={<PostForm />}
//                       />
//                       <Route
//                         path="/pitech/blog/posts/edit/:id"
//                         element={<PostForm />}
//                       />
//                       <Route
//                         path="/pitech/blog/categories"
//                         element={<CategoriesManagement />}
//                       />
//                       <Route
//                         path="/pitech/blog/tags"
//                         element={<TagsManagement />}
//                       />

//                       {/* Invoice Routes */}
//                       <Route path="/invoices" element={<InvoiceManagement />} />
//                       <Route
//                         path="/invoice/pay/:paymentId"
//                         element={<InvoicePayment />}
//                       />

//                       {/* Booking Routes */}
//                       <Route path="/book/:slug" element={<BookingPage />} />
//                       <Route
//                         path="/booking-confirmation/:bookingNumber"
//                         element={<BookingConfirmation />}
//                       />

//                       {/* Admin Booking Routes */}
//                       <Route
//                         path="/pitech/bookings"
//                         element={<BookingManagement />}
//                       />

//                       {!(
//                         process.env.NODE_ENV === "dev" ||
//                         window.location.hostname === "dev.bagbox.ca"
//                       ) && (
//                           <>
//                             <Route path="/sitemap.xml" element={<SitemapXml />} />
//                             <Route path="/sitemap" element={<SitemapXml />} />
//                           </>
//                         )}

//                       <Route
//                         path="/admin/*"
//                         element={
//                           <Navigate
//                             to={window.location.pathname.replace(
//                               /^\/admin/,
//                               "/pitech"
//                             )}
//                             replace
//                           />
//                         }
//                       />

//                       {/* Google OAuth Routes */}
//                       {/* <Route path="/pitech/auth-success" element={<AuthSuccess />} />
//                       <Route path="/pitech/auth-error" element={<AuthError />} /> */}
//                     </Routes>
//                   </main>
//                   <Footer style={{}} />
//                 </div>
//                 <BackToTopButton />
//               </Router>
//             </ConfigStylesProvider>
//           </ConfigProvider>
//         </NotificationProvider>
//       </CartProvider>
//     </AuthProvider>
//   );
// }

// export default App;

// //working properly
// // // src/App.js
// // import React from "react";
// // import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
// // import { Helmet } from "react-helmet";
// // import "./styles/main.css";

// // //components
// // import Navbar from "./components/Navbar";
// // import Footer from "./components/Footer";
// // import ScrollToTop from "./components/ScrollToTop";
// // import BackToTopButton from "./components/BackToTopButton";
// // import GlobalNotification from "./components/GlobalNotification";
// // import CategoryPage from "./pages/CategoryPage";
// // import SitemapXml from './components/SitemapXml';

// // import MenuManagement from "./components/admin/MenuManagement";

// // //Context
// // import { AuthProvider } from "./context/AuthContext";
// // import { CartProvider } from "./context/CartContext";
// // import { NotificationProvider } from "./context/NotificationContext";
// // import { ConfigProvider } from "./context/ConfigContext";
// // import { ConfigStylesProvider } from './context/ConfigStylesProvider';

// // //pages
// // // import Home from "./pages/Home";
// // // import AboutUs from "./pages/AboutUs";
// // // import Contact from "./pages/Contact";
// // // import Product from "./pages/Product";
// // // import PrivacyPolicy from "./pages/PrivacyPolicy";
// // // import TermsConditions from "./pages/TermsConditions";
// // import Login from "./pages/Login";
// // import Register from "./pages/Register";
// // import Profile from "./pages/Profile";
// // import Orders from "./pages/Orders";
// // import Cart from "./pages/Cart";
// // import ForgotPassword from './pages/ForgotPassword';
// // import ResetPassword from './pages/ResetPassword';
// // import ProductCustomization from "./pages/ProductCustomization";
// // import AdminDashboard from "./pages/AdminDashboard";
// // import ClientUploads from './pages/admin/ClientUploads';
// // import Unsubscribe from './pages/Unsubscribe';
// // import Support from "./pages/Support";
// // import Page from "./pages/Page";
// // import TeamMemberDetail from "./pages/TeamMemberDetail";
// // import TeamPage from "./pages/TeamPage";
// // import NotificationsPage from "./pages/NotificationsPage";
// // import OptimizedHomePage from "./pages/OptimizedHomePage"

// // // Blog Pages
// // import Blog from "./pages/blog/Blog";
// // import BlogPost from "./pages/blog/BlogPost";
// // import BlogCategory from "./pages/blog/BlogCategory";
// // import BlogTag from "./pages/blog/BlogTag";

// // // Blog Management Components
// // import BlogManagement from "./components/admin/BlogManagement";
// // import BlogDashboard from "./components/admin/blog/Dashboard";
// // import PostsManagement from "./components/admin/blog/PostsManagement";
// // import PostForm from "./components/admin/blog/PostForm";
// // import CategoriesManagement from "./components/admin/blog/CategoriesManagement";
// // import TagsManagement from "./components/admin/blog/TagsManagement";
// // import NewClientForm from './components/admin/NewClientForm';
// // import InvoiceManagement from './components/admin/InvoiceManagement';
// // import InvoicePayment from './pages/InvoicePayment';

// // function App() {

// //   return (
// //     <AuthProvider>
// //       <CartProvider>
// //         <NotificationProvider>
// //           <ConfigProvider>
// //             <ConfigStylesProvider>
// //               <Router>
// //                 <ScrollToTop />
// //                 {/* Conditionally add meta tag to prevent search engine indexing in development */}
// //                 {(process.env.NODE_ENV==="dev"|| window.location.hostname === "dev.bagbox.ca") && (
// //                   <Helmet>
// //                     <meta name="robots" content="noindex, nofollow" />
// //                   </Helmet>
// //                 )}
// //                 <div className="min-h-screen bg-gray-50">
// //                 <Navbar menuName="navbar" fallbackMenuName="main" />
// //                 <GlobalNotification />
// //                   <main
// //                     className="container mx-auto px-4 py-8"
// //                     style={{ minWidth: 100 + "%", padding: 0 }}
// //                   >
// //                     <Routes>
// //                     <Route path="/" element={<OptimizedHomePage />} />
// //                       <Route path="/:slug" element={<Page />} />
// //                       {/* <Route path="/" element={
// //                         <PageBuilderRenderer pageKey="home" fallback={<Home />} />
// //                       } /> */}
// //                       {/* <Route path="/" element={<Home />} /> */}
// //                       {/* <Route path="/product/:productName" element={<ProductCustomization />} /> */}
// //                       {/* <Route path="/product/:productSlug" element={<ProductCustomization />} /> */}
// //                       {/* <Route path="/product/:productName/:id" element={<ProductCustomization />} /> */}
// //                       {/* Home route now uses PageBuilderRenderer with fallback to Home component */}

// //                       <Route path="/product/:productSlug" element={<ProductCustomization />} />

// //                       {/* <Route path="/customize/:productId" element={<ProductCustomization />} /> */}
// //                       {/* <Route path="/customize/:productId" element={<ProductEditor />} /> */}
// //                       {/* <Route path="/customize/:productId" element={<ProductEditorRoute />} /> */}
// //                       <Route path="/login" element={<Login />} />
// //                       <Route path="/register" element={<Register />} />
// //                       <Route path="/cart" element={<Cart />} />
// //                       <Route path="/pitech" element={<AdminDashboard />} />
// //                       <Route path="/profile" element={<Profile />} />
// //                       <Route path="/orders" element={<Orders />} />
// //                       <Route path="/cat/:categorySlug" element={<CategoryPage />} />

// //                       <Route path="/team/" element={<TeamPage />} />
// //                       <Route path="/team/:slug" element={<TeamMemberDetail />} />
// //                       <Route path="/support" element={<Support />} />
// //                       <Route path="/notifications" element={<NotificationsPage />} />

// //                       <Route path="/forgot-password" element={<ForgotPassword />} />
// //                       <Route path="/reset-password/:token" element={<ResetPassword />} />
// //                       <Route path="/unsubscribe/:token" element={<Unsubscribe />} />

// //                       <Route path="/blog" element={<Blog />} />
// //                       <Route path="/blog/:slug" element={<BlogPost />} />
// //                       <Route path="/blog/category/:slug" element={<BlogCategory />} />
// //                       <Route path="/blog/tag/:slug" element={<BlogTag />} />

// //                       {/* Admin dashboard */}
// //                       <Route path="/pitech/clients/new" element={<NewClientForm />} />
// //                       <Route path="/pitech/uploads" element={<ClientUploads />} />
// //                       <Route path="/pitech/menus" element={<MenuManagement />} />
// //                       {/* <Route path="/pitech/pages" element={<PageManagement />} />
// //                       <Route path="/pitech/pages/:id" element={<PageManagement />} />  */}
// //                       <Route path="/pitech/pages" element={<AdminDashboard initialActiveMenu="pageManagement" />} />
// //                       <Route path="/pitech/pages/:id" element={<AdminDashboard initialActiveMenu="pageManagement" />} />
// //                       {/* Blog Management Routes */}
// //                       <Route path="/pitech/blog" element={<BlogManagement />} />
// //                       <Route path="/pitech/blog/dashboard" element={<BlogDashboard />} />
// //                       <Route path="/pitech/blog/posts" element={<PostsManagement />} />
// //                       <Route path="/pitech/blog/posts/new" element={<PostForm />} />
// //                       <Route path="/pitech/blog/posts/edit/:id" element={<PostForm />} />
// //                       <Route path="/pitech/blog/categories" element={<CategoriesManagement />} />
// //                       <Route path="/pitech/blog/tags" element={<TagsManagement />} />

// //                       {/* Invoice Routes */}
// //                       <Route path="/invoices" element={<InvoiceManagement />} />
// //                       <Route path="/invoice/pay/:paymentId" element={<InvoicePayment />} />

// //                       {!(process.env.NODE_ENV === "dev" ||
// //                         window.location.hostname === "dev.bagbox.ca") && (
// //                           <>
// //                         <Route path="/sitemap.xml" element={<SitemapXml />} />
// //                         <Route path="/sitemap" element={<SitemapXml />} />
// //                         </>
// //                       )}

// //                       <Route
// //                         path="/admin/*"
// //                         element={<Navigate to={window.location.pathname.replace(/^\/admin/, '/pitech')} replace />}
// //                       />

// //                     </Routes>
// //                   </main>
// //                   <Footer style={{}} />
// //                 </div>
// //                 <BackToTopButton />
// //               </Router>
// //             </ConfigStylesProvider>
// //           </ConfigProvider>
// //         </NotificationProvider>
// //       </CartProvider>
// //     </AuthProvider>
// //   );
// // }

// // export default App;
